import { useAppDispatch, useAppSelector } from "../Store/store";
import { CreateGtSessionVideoSdkDto, UpdateGtSessionVideoSdkDto, apiSlice, useGoodThoughtsControllerAddPatientToSessionOnlyByUserIdMutation, useGoodThoughtsControllerCreateSessionVideoSdkMutation, useGoodThoughtsControllerDeletePatientByProviderMutation, useGoodThoughtsControllerDeleteSessionVideoSdkMutation, useGoodThoughtsControllerUpdatePatientByProviderMutation, useGoodThoughtsControllerUpdateSessionVideoSdkMutation} from "../Features/API/apiSlice.generated"
import { NotificationType, useNotification } from "../Features/Notification"
import { getVidSdkAuth } from "../Features/VideoSdkAuth/videoSdkAuthSlice";
import { useModal } from "./useModal";
import { useState } from "react";
import { GTSessionType } from "../Features/API/manualDefines";
import { canValidateVideoSdkRoom } from "../helpers/videoSdkValidation";

export const useMeet = () => {
    const dispatch = useAppDispatch()
    const API_BASE_URL = "https://api.videosdk.live";
    const {notify} = useNotification()
    const {dismissModal} = useModal()
    const {data:tokenObj} = useAppSelector(getVidSdkAuth)
    const token = (tokenObj as any)?.token || ""

    const [createMeet , {isLoading : addingMeet}] = useGoodThoughtsControllerCreateSessionVideoSdkMutation()
    const [updateMeet] = useGoodThoughtsControllerUpdateSessionVideoSdkMutation()
    const [deleteMeet] = useGoodThoughtsControllerDeleteSessionVideoSdkMutation()
    const [addPatient , {isLoading:addingPatient}] = useGoodThoughtsControllerAddPatientToSessionOnlyByUserIdMutation()
    const [deletePatient , {isLoading : deletingPatient}] = useGoodThoughtsControllerDeletePatientByProviderMutation()
    const [updatePatient] = useGoodThoughtsControllerUpdatePatientByProviderMutation()
    const [addingMeetLoading,setAddingMeetLoading] = useState<boolean>(false)

    const createRoom = async (authToken:string) => {
        const url = `${API_BASE_URL}/v2/rooms`;
        const options = {
          method: "POST",
          headers: { Authorization: authToken, "Content-Type": "application/json" },
        };
      
        const room = await fetch(url, options)
          .then((response) => response.json())
          .catch((error) => console.error("error", error));
      
        return room
    };

    const validateMeeting = async ({ roomId, token }) => {
      if (!canValidateVideoSdkRoom({ roomId, token })) {
        return false
      }

      const url = `${API_BASE_URL}/v2/rooms/validate/${roomId}`;
    
      const options = {
        method: "GET",
        headers: { Authorization: token, "Content-Type": "application/json" },
      };
    
      const result = await fetch(url, options)
        .then((response) =>  {
          if(response.ok && response.status === 200) {
            return response.json()
          } else {
             notify("Third-party VideoSDK error", NotificationType.ERROR)
          }
        }) 
        .catch((error) => console.error("error", error));
        // console.log("Validated Room Data: ", result)
        return result ? result?.roomId === roomId : false
    };

    const handleCreate = async (values) => {
        setAddingMeetLoading(true);
        const room = await createRoom(token)
        if(room?.roomId !== "") {
            const formatDate = values?.meetingDate.toLocaleString('en-US', {
              year: 'numeric',
              month: '2-digit',
              day: '2-digit',
            })
            const time = values?.meetingTime
            const localDateTimeString = `${formatDate} ${time}`
            const localDateTimeObj = new Date(Date.parse(localDateTimeString))

            const vrEnabled = values?.vrEnabled
            const anonAvatars = values?.anonAvatars
            console.log('VR enabled: ', vrEnabled)
            console.log('Anon Avatars: ', anonAvatars)

            // DTO potential data
            var session_type;
            if(!vrEnabled && !anonAvatars) {
              session_type = GTSessionType.TELEHEALTH
            } else if(!vrEnabled && anonAvatars) {
              session_type = GTSessionType.AVATAR_TELEHEALTH
            } else {
              session_type = GTSessionType.DEFAULT
            }
                      
            const createGtSessionVideoSdkDto:CreateGtSessionVideoSdkDto = {
                name : values?.meetingName,
                description : values?.meetingDescription,
                startAt: localDateTimeObj.toISOString(),
                room: room,
                sessionIsActive: false,
                spaceFromTemplate : values?.template?.value,
                type: session_type
            }

            if(values?.cronExpression !== "") {
              createGtSessionVideoSdkDto.cronExpression = values?.cronExpression
            }
            try {
              const res = await createMeet({createGtSessionVideoSdkDto}).unwrap()
              dismissModal()
              dispatch(apiSlice.util.invalidateTags(['ClientMeetings']))
              return res;
            } catch (error) {
              notify("Something went wrong!", NotificationType.ERROR)
            }
            finally{
              setAddingMeetLoading(false);
            }
        }
    }

    const handleUpdate = async (meetId, values, room) => {
       const roomId = room?.roomId
       const isRoomValid = await validateMeeting({roomId, token})
       if(isRoomValid) {
          const formatDate = values?.meetingDate.toLocaleString('en-US', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
          })
          const time = values?.meetingTime
          const localDateTimeString = `${formatDate} ${time}`
          const localDateTimeObj = new Date(Date.parse(localDateTimeString))
          const updateGtSessionVideoSdkDto:UpdateGtSessionVideoSdkDto = {
            name : values?.meetingName,
            description : values?.meetingDescription,
            startAt: localDateTimeObj.toISOString(),
            room: room,
            type: "DEFAULT"
          }
          try {
            await updateMeet({id:meetId, updateGtSessionVideoSdkDto})
            dismissModal()
            notify("Meeting updated successfully", NotificationType.SUCCESS)
            dispatch(apiSlice.util.invalidateTags(['ClientMeetings']))
          } catch (error) {
            notify("Something went wrong!", NotificationType.ERROR)
          }
       } else {
         notify("This session is expired!", NotificationType.ERROR)
       }
    }

    const handleDelete = async (id:string) => {
      if (!id) {
        notify("Cannot delete: session ID is missing", NotificationType.ERROR)
        return
      }
      try {
        await deleteMeet({id}).unwrap()
        dismissModal()
        notify("Meeting deleted successfully", NotificationType.SUCCESS)
        // Invalidate both client and provider meetings cache
        dispatch(apiSlice.util.invalidateTags(['ClientMeetings', 'ProviderMeetings']))
      } catch (error) {
        console.error('Delete failed:', error)
        notify("Something went wrong!", NotificationType.ERROR)
      }
    }

    const handleAddPatient = async(sessionId:string) => {
         try {
            await addPatient({sessionId}).unwrap();
         } catch (error:any) {
            if (error?.data) {
              notify(error.data.message, NotificationType.ERROR);
            } else {
              notify("Something went wrong!", NotificationType.ERROR);
            }
         }
    }

    const handleDeletePatient = async (sessionId:string, patientId:string) => {
        try {
          await deletePatient({sessionId, patientId})
          notify("Patient deleted successfully", NotificationType.SUCCESS);
        } catch (error:any) {
          if (error?.data) {
            notify(error.data.message, NotificationType.ERROR);
          } else {
            notify("Something went wrong!", NotificationType.ERROR);
          }
        }
    }

    const handleUpdatePatient = async(sessionId:string, patient) => {
        const patientId = patient?.id;
        const updatePatientDto = {
          name: patient?.name,
          address: "",
          primaryDiagnosis : localStorage.getItem('primaryDiagnosis') || "Substance Use Disorder",
          facilityLocation: "",
          dateOfAdmission: patient?.createdAt
        }
        await updatePatient({sessionId, patientId, updatePatientDto})
    }

    return {
        handleCreate,
        addingMeet,
        handleUpdate,
        handleDelete,
        validateMeeting,
        handleAddPatient,
        addingPatient,
        handleDeletePatient,
        deletingPatient,
        handleUpdatePatient,
        addingMeetLoading,
        // patientsLoading,
        // patientsError,
        // patients
    } 
}
